/**
 * CreateBookingModalShell — the "New booking" modal's visual shell (BON-142):
 * a right-docked panel on desktop, a full-screen sheet on mobile. Owns the
 * chrome + submit button and blocks closing while a create is in flight;
 * `CreateBookingModalFields` owns the form body.
 */
import { useEffect, useId } from 'react';

import {
  CreateBookingModalFields,
  type CreateBookingFieldsProps,
} from './CreateBookingModalFields';
import { CreateBookingModalShellFooter } from './CreateBookingModalShellFooter';
import { CreateBookingModalShellHeader } from './CreateBookingModalShellHeader';

export interface CreateBookingModalShellProps {
  fields: CreateBookingFieldsProps;
  error: string | null;
  isSubmitting: boolean;
  submitDisabled: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
}

export function CreateBookingModalShell({
  fields,
  error,
  isSubmitting,
  submitDisabled,
  onClose,
  onSubmit,
}: CreateBookingModalShellProps) {
  const titleId = useId();

  // Blocked while a create is in flight — Escape, the backdrop and the X used
  // to close mid-request, which yanked whoever walked away mid-submit onto
  // whatever came next once it resolved.
  const guardedClose = () => {
    if (!isSubmitting) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isSubmitting]);

  return (
    <div
      role="presentation"
      onClick={guardedClose}
      className="fixed inset-0 z-50 flex justify-end bg-black/50"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl dark:bg-[#1a1f1c] sm:max-w-md lg:max-w-lg"
      >
        <CreateBookingModalShellHeader
          onClose={guardedClose}
          titleId={titleId}
          isSubmitting={isSubmitting}
        />
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 min-h-0 space-y-5 overflow-y-auto px-5 py-5">
            <CreateBookingModalFields {...fields} />
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          </div>
          <CreateBookingModalShellFooter
            hasWorkshop={!!fields.workshop}
            mode={fields.mode}
            isSubmitting={isSubmitting}
            disabled={submitDisabled}
            onClose={guardedClose}
          />
        </form>
      </div>
    </div>
  );
}
